import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAdminAppearance } from '../../contexts/AdminAppearanceContext';
import '../../styles/global.css';

interface DyeDemandRow {
  item_id: number;
  week: string;
  line_id: number;
  line_name: string;
  material: string | null;
  marka: string | null;
  indeks_kat: string | null;
  nazwa_oryg: string | null;
  kolor: string;
  barwnik: string;
  dye_key: string;
  doz_procent: string;
  waga: string | null;
  weight_kg: number;
  do_produkcji: number;
  wyprodukowano: number;
  pozostalo: number;
  status: string;
  /** 'available' | 'missing' | null — dye availability marked by a person. */
  barwnik_status: 'available' | 'missing' | null;
  dye_kg: number;
  dye_kg_remaining: number;
  dosage_missing: boolean;
  /** Ile barwnikow ma cala pozycja — przy >1 wiersz nie wchodzi do sum per barwnik. */
  dye_count: number;
}

/** Zapotrzebowanie zsumowane na kod barwnika (tylko pozycje z jednym barwnikiem). */
interface DyeGroup {
  barwnik: string;
  dye_key: string;
  rows: number;
  items: number;
  dye_kg: number;
  dye_kg_remaining: number;
  missing_rows: number;
  dosage_missing: boolean;
}

/** To, co zostalo poza sumami: pozycje z kilkoma barwnikami. */
interface MultiDyeSummary {
  rows: number;
  items: number;
  dye_kg: number;
  dye_kg_remaining: number;
}

interface ProductionLineOption {
  id: number;
  name: string;
}

interface DyeDemandResponse {
  results: DyeDemandRow[];
  dye_groups: DyeGroup[];
  multi_dye: MultiDyeSummary;
  total: number;
  total_dye_kg: number;
  total_dye_kg_remaining: number;
  missing_rows: number;
  truncated: boolean;
  available_weeks: string[];
  available_lines: ProductionLineOption[];
}

/** Rows rendered at once — the unfiltered list runs into thousands and would stall the tablet. */
const PAGE_SIZE = 300;

const EMPTY: DyeDemandResponse = {
  results: [],
  dye_groups: [],
  multi_dye: { rows: 0, items: 0, dye_kg: 0, dye_kg_remaining: 0 },
  total: 0,
  total_dye_kg: 0,
  total_dye_kg_remaining: 0,
  missing_rows: 0,
  truncated: false,
  available_weeks: [],
  available_lines: [],
};

/* Wyglad tabeli 1:1 z „Moja produkcja" (ProductionTable): cienkie ramki, drobny font,
   naglowki przyklejone do gory, a kluczowe kolumny kilogramowe przyklejone do prawej. */
const commonCellStyle: React.CSSProperties = {
  padding: '10px',
  border: '1px solid #dee2e6',
  fontSize: '10px',
};

/** Waskie kolumny — ta sama wartosc co narrowLeftWidth w ProductionTable. */
const NARROW_WIDTH = 55;

/** Kolumny przyklejone do prawej — jak DO PRODUKCJI / POZOSTALO w „Moja produkcja". */
const STICKY_WIDTH = { dyeKg: 78, dyeKgRemaining: 92 } as const;

const scrollCellStyle = (width: number): React.CSSProperties => ({
  ...commonCellStyle,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width,
  minWidth: width,
  maxWidth: width,
});

const scrollHeaderStyle = (width: number): React.CSSProperties => ({
  ...commonCellStyle,
  textAlign: 'left',
  wordBreak: 'break-word',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width,
  minWidth: width,
  maxWidth: width,
  position: 'sticky',
  top: 0,
  zIndex: 3,
  backgroundColor: '#f8f9fa',
  boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)',
});

const stickyCellStyle = (right: number, width: number, bgColor: string, borderLeft = false): React.CSSProperties => ({
  ...commonCellStyle,
  padding: '5px 4px',
  textAlign: 'right',
  position: 'sticky',
  right,
  zIndex: 1,
  backgroundColor: bgColor,
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: 'border-box',
  overflow: 'hidden',
  ...(borderLeft && { borderLeft: '3px solid #000' }),
});

const stickyHeaderStyle = (right: number, width: number, bgColor: string, color: string, borderLeft = false): React.CSSProperties => ({
  ...commonCellStyle,
  padding: '5px 4px',
  textAlign: 'center',
  position: 'sticky',
  top: 0,
  right,
  zIndex: 4,
  backgroundColor: bgColor,
  color,
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: 'border-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)',
  ...(borderLeft && { borderLeft: '3px solid #000' }),
});

/**
 * Kolumny przewijane, w kolejnosci wyswietlania. Naglowki bierzemy z `table.*`, zeby
 * zapotrzebowanie nazywalo te same rzeczy tak samo jak panele linii.
 *
 * `narrow` = staly waski slupek (jak Waga / DOZ. % na linii), `width` = wartosc wlasna,
 * a bez obu kolumna idzie na szerokosc z ustawien „Wyglad" — dokladnie jak ProductionTable.
 */
const SCROLL_COLUMNS: Array<{ key: string; labelKey: string; narrow?: boolean; width?: number }> = [
  { key: 'week', labelKey: 'table.week', width: 60 },
  { key: 'line', labelKey: 'table.line', width: 60 },
  { key: 'material', labelKey: 'table.material' },
  { key: 'brand', labelKey: 'table.brand' },
  { key: 'index', labelKey: 'table.catalogIndex' },
  { key: 'color', labelKey: 'table.color' },
  { key: 'dye', labelKey: 'table.dye' },
  { key: 'dyeStatus', labelKey: 'dyeDemand.colDyeStatus', width: 96 },
  { key: 'dosage', labelKey: 'table.dosagePercent', width: 70 },
  { key: 'weight', labelKey: 'table.weight', narrow: true },
  { key: 'toProduce', labelKey: 'table.toProduce', width: 78 },
  { key: 'remainingQty', labelKey: 'dyeDemand.colRemainingQty', width: 65 },
];

/**
 * Pozycja, przy ktorej ktos juz pracuje — barwnik fizycznie jest, wiec wiersz swieci sie na
 * zielono bez recznego oznaczania. Jawne „Brak" zostawiamy na czerwono: to zgloszony problem,
 * ktorego automat nie ma prawa zamiesc pod dywan.
 */
const isStarted = (row: DyeDemandRow): boolean =>
  row.status === 'in_progress' || row.wyprodukowano > 0;

/** Wiersz podsumowania barwnika — wyrozniony pasek otwierajacy grupe pozycji. */
const groupRowCell: React.CSSProperties = {
  ...commonCellStyle,
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: 700,
  borderTop: '2px solid #667eea',
};

const formatKg = (kg: number): string =>
  `${kg.toLocaleString('pl-PL', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;

const selectStyle: React.CSSProperties = {
  padding: '10px 14px',
  border: '2px solid #e1e8ed',
  borderRadius: '8px',
  fontSize: '14px',
  color: '#4a5568',
  backgroundColor: 'white',
  outline: 'none',
  minWidth: '180px',
};

/**
 * How much dye is needed to finish the positions still open on the lines.
 *
 * Only positions that are not produced yet are listed (pending + in progress). Multi-colour
 * positions are expanded by the backend into one row per dye, so the same catalogue index
 * can appear several times with different dye codes.
 */
const DyeDemand: React.FC = () => {
  const { t } = useLanguage();
  // Te same szerokosci kolumn co „Moja produkcja" — ustawiane w panelu „Wyglad".
  const { columnWidthLeft } = useAdminAppearance();

  const columnWidth = (col: { narrow?: boolean; width?: number }): number =>
    col.width ?? (col.narrow ? NARROW_WIDTH : columnWidthLeft);
  /** Szerokosc kolumny przewijanej po jej kluczu. */
  const w = (key: string): number => {
    const col = SCROLL_COLUMNS.find((c) => c.key === key);
    return col ? columnWidth(col) : columnWidthLeft;
  };
  const tableMinWidth = SCROLL_COLUMNS.reduce((sum, col) => sum + columnWidth(col), 0)
    + STICKY_WIDTH.dyeKg + STICKY_WIDTH.dyeKgRemaining;
  const [data, setData] = useState<DyeDemandResponse>(EMPTY);
  const [lineFilter, setLineFilter] = useState('');
  const [weekFilter, setWeekFilter] = useState('');
  const [dyeStatusFilter, setDyeStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  /** Item ids currently being saved — the row's buttons are disabled meanwhile. */
  const [savingIds, setSavingIds] = useState<number[]>([]);
  /** Rozwiniete grupy barwnikow — domyslnie widac same sumy. */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /** Grupy, w ktorych operator poprosil o wszystkie wiersze (domyslnie pierwsze PAGE_SIZE). */
  const [showAllIn, setShowAllIn] = useState<Set<string>>(new Set());
  /** Grupy z trwajacym zapisem zbiorczym — przyciski sa wtedy zablokowane. */
  const [savingGroups, setSavingGroups] = useState<Set<string>>(new Set());

  /**
   * Pozycje ustawione pod kod barwnika. Sumy bierzemy z `dye_groups` (liczy je backend na
   * calym zbiorze), a tutaj dokladamy do nich wiersze do rozwiniecia — dzieki temu lista
   * jest jedna: podsumowanie barwnika i jego pozycje w tej samej tabeli.
   */
  const { byDye, multiDyeRows, noDyeRows } = useMemo(() => {
    const map = new Map<string, DyeDemandRow[]>();
    const multi: DyeDemandRow[] = [];
    const none: DyeDemandRow[] = [];

    for (const row of data.results) {
      if (!row.barwnik) {
        none.push(row);
      } else if (row.dye_count > 1) {
        multi.push(row);
      } else {
        const rows = map.get(row.dye_key);
        if (rows) {
          rows.push(row);
        } else {
          map.set(row.dye_key, [row]);
        }
      }
    }

    return { byDye: map, multiDyeRows: multi, noDyeRows: none };
  }, [data.results]);

  const toggleGroup = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (lineFilter) params.set('line_id', lineFilter);
      if (weekFilter) params.set('week', weekFilter);
      if (dyeStatusFilter !== 'all') params.set('dye_status', dyeStatusFilter);

      const res = await api.get(`/dye-demand?${params.toString()}`);
      setData(res.data);
      setExpanded(new Set());
      setShowAllIn(new Set());
    } catch (err) {
      console.error('Dye demand error:', err);
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [lineFilter, weekFilter, dyeStatusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * The flag lives on the position, not on the dye code, so a multi-colour position shares one
   * status across all of its dye rows — every row of that item is updated at once locally.
   */
  const setDyeStatus = async (itemId: number, next: 'available' | 'missing' | 'unknown') => {
    setSavingIds((ids) => [...ids, itemId]);
    try {
      await api.put(`/production-items/${itemId}/barwnik-dostarczony`, { status: next });
      const applied = next === 'unknown' ? null : next;
      setData((prev) => ({
        ...prev,
        results: prev.results.map((row) =>
          row.item_id === itemId ? { ...row, barwnik_status: applied } : row
        ),
      }));
    } catch (err: any) {
      alert(`${t('common.error')}: ${err.response?.data?.message || ''}`);
    } finally {
      setSavingIds((ids) => ids.filter((id) => id !== itemId));
    }
  };

  /**
   * Odhaczenie CALEGO barwnika: jeden request na komplet pozycji zamiast requestu na indeks
   * (jeden kod potrafi siedziec na setkach pozycji). Stan w tabeli podmieniamy lokalnie,
   * zeby nie przeladowywac calej listy po kazdym klknieciu.
   */
  const setGroupDyeStatus = async (
    key: string,
    rows: DyeDemandRow[],
    next: 'available' | 'missing' | 'unknown',
  ) => {
    const itemIds = Array.from(new Set(rows.map((row) => row.item_id)));
    if (itemIds.length === 0) return;

    setSavingGroups((prev) => new Set(prev).add(key));
    try {
      await api.put('/production-items/barwnik-dostarczony/bulk', { item_ids: itemIds, status: next });
      const applied = next === 'unknown' ? null : next;
      const touched = new Set(itemIds);
      setData((prev) => ({
        ...prev,
        results: prev.results.map((row) =>
          touched.has(row.item_id) ? { ...row, barwnik_status: applied } : row
        ),
      }));
    } catch (err: any) {
      alert(`${t('common.error')}: ${err.response?.data?.message || ''}`);
    } finally {
      setSavingGroups((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(key);
        return nextSet;
      });
    }
  };

  /**
   * Wiersz podsumowania barwnika + jego pozycje po rozwinieciu. Jedna tabela: sumy i
   * szczegoly siedza w tej samej liscie, a nie w osobnym zestawieniu obok.
   */
  const renderGroup = (
    key: string,
    label: string,
    items: number,
    dyeKg: number,
    dyeKgRemaining: number,
    rows: DyeDemandRow[],
    hasMissing: boolean,
    dosageMissing: boolean,
    excluded = false,
  ) => {
    const isOpen = expanded.has(key);
    const limit = showAllIn.has(key) ? rows.length : PAGE_SIZE;
    const shown = isOpen ? rows.slice(0, limit) : [];
    const savingGroup = savingGroups.has(key);

    // Przycisk swieci sie tylko wtedy, gdy CALA grupa ma ten sam stan — mieszanka zostaje
    // nieoznaczona, zeby jeden odhaczony indeks nie udawal odhaczonego barwnika.
    const groupStatus = rows.length > 0
      && rows.every((row) => row.barwnik_status === rows[0].barwnik_status)
      ? rows[0].barwnik_status
      : null;

    // Zielony takze wtedy, gdy wszystkie pozycje barwnika sa juz w produkcji.
    const allStarted = rows.length > 0 && rows.every((row) => isStarted(row) || row.barwnik_status === 'available');
    const groupColor = hasMissing || groupStatus === 'missing'
      ? '#f8d7da'
      : (groupStatus === 'available' || allStarted) ? '#d4edda' : '#eef2ff';

    return (
      <React.Fragment key={key}>
        <tr style={{ backgroundColor: groupColor }}>
          <td colSpan={SCROLL_COLUMNS.length} style={{ ...groupRowCell, backgroundColor: groupColor }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {/* Podglad rozwija indeksy skladajace sie na te sume — sam wiersz zostaje
                  klikalny tylko przez ten przycisk, zeby odhaczanie nie otwieralo listy. */}
              <button
                type="button"
                onClick={() => toggleGroup(key)}
                style={{
                  padding: '3px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px',
                  border: '1px solid #667eea', backgroundColor: isOpen ? '#667eea' : 'white',
                  color: isOpen ? 'white' : '#667eea', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {isOpen ? '▾' : '▸'} {t('dyeDemand.groupPreview')}
              </button>

              <span>{label}</span>

              {dosageMissing && (
                <span style={{ color: '#dc3545' }} title={t('dyeDemand.dosageMissingHint')}>
                  &#9888;&#65039;
                </span>
              )}

              <span style={{ fontWeight: 400, color: '#475569' }}>
                {t('dyeDemand.groupsColPositions')}: {items.toLocaleString('pl-PL')}
              </span>

              {excluded ? (
                <span style={{ fontWeight: 400, color: '#b45309' }}>
                  {t('dyeDemand.groupsExcludedHint')}
                </span>
              ) : (
                /* Odhaczenie calego barwnika naraz — jeden klik zamiast indeks po indeksie. */
                <span style={{ display: 'inline-flex', gap: 4, marginLeft: 'auto' }}>
                  {([
                    { value: 'available' as const, label: t('dyeDemand.markAvailable'), active: '#16a34a' },
                    { value: 'missing' as const, label: t('dyeDemand.markMissing'), active: '#dc2626' },
                  ]).map(({ value, label: btnLabel, active }) => {
                    const isActive = groupStatus === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        disabled={savingGroup}
                        // Ponowny klik w aktywny stan cofa oznaczenie calej grupy do „niesprawdzone".
                        onClick={() => setGroupDyeStatus(key, rows, isActive ? 'unknown' : value)}
                        style={{
                          padding: '3px 10px', fontSize: '11px', fontWeight: 'bold', borderRadius: '4px',
                          cursor: savingGroup ? 'wait' : 'pointer', opacity: savingGroup ? 0.5 : 1,
                          border: `1px solid ${isActive ? active : '#adb5bd'}`,
                          backgroundColor: isActive ? active : 'white',
                          color: isActive ? 'white' : '#495057',
                        }}
                      >
                        {btnLabel}
                      </button>
                    );
                  })}
                </span>
              )}
            </div>
          </td>
          <td style={{ ...stickyCellStyle(STICKY_WIDTH.dyeKgRemaining, STICKY_WIDTH.dyeKg, '#fef3c7', true), fontWeight: 700 }}>
            {excluded ? '-' : formatKg(dyeKg)}
          </td>
          <td style={{ ...stickyCellStyle(0, STICKY_WIDTH.dyeKgRemaining, '#dbeafe'), fontWeight: 700 }}>
            {excluded ? '-' : formatKg(dyeKgRemaining)}
          </td>
        </tr>

        {shown.map((row, idx) => renderRow(row, idx))}

        {isOpen && rows.length > shown.length && (
          <tr>
            <td colSpan={SCROLL_COLUMNS.length + 2} style={{ ...commonCellStyle, textAlign: 'center', padding: '10px' }}>
              <span style={{ fontSize: '12px', color: '#64748b', marginRight: 10 }}>
                {t('dyeDemand.shownCount', {
                  shown: shown.length.toLocaleString('pl-PL'),
                  total: rows.length.toLocaleString('pl-PL'),
                })}
              </span>
              <button
                type="button"
                onClick={() => setShowAllIn((prev) => new Set(prev).add(key))}
                style={{
                  padding: '5px 12px', backgroundColor: 'white', color: '#667eea',
                  border: '1px solid #667eea', borderRadius: '6px', cursor: 'pointer',
                  fontWeight: 600, fontSize: '12px',
                }}
              >
                {t('dyeDemand.showAll')}
              </button>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  /** Pojedyncza pozycja wewnatrz grupy barwnika. */
  const renderRow = (row: DyeDemandRow, idx: number) => {
                // Tlo wiersza niesie stan barwnika — zielony gdy jest, czerwony gdy brakuje.
                const rowColor = row.barwnik_status === 'missing'
                  ? '#f8d7da'
                  : (row.barwnik_status === 'available' || isStarted(row)) ? '#d4edda' : 'white';
                const saving = savingIds.includes(row.item_id);

                return (
                  <tr
                    key={`${row.item_id}-${row.barwnik}-${idx}`}
                    style={{
                      borderBottom: '2px solid #000',
                      backgroundColor: rowColor,
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = rowColor; }}
                  >
                    <td style={scrollCellStyle(w('week'))}>{row.week}</td>
                    <td style={scrollCellStyle(w('line'))}>{row.line_name}</td>
                    <td style={scrollCellStyle(w('material'))}>{row.material || '-'}</td>
                    <td style={scrollCellStyle(w('brand'))}>{row.marka || '-'}</td>
                    <td style={{ ...scrollCellStyle(w('index')), fontWeight: 'bold' }}>{row.indeks_kat || '-'}</td>
                    <td style={scrollCellStyle(w('color'))}>{row.kolor || '-'}</td>
                    <td style={scrollCellStyle(w('dye'))}>{row.barwnik || '-'}</td>
                    <td style={{ ...scrollCellStyle(w('dyeStatus')), textAlign: 'center', padding: '5px 4px' }}>
                      <div style={{ display: 'inline-flex', gap: '3px' }}>
                        {([
                          { value: 'available' as const, label: t('dyeDemand.markAvailable'), active: '#16a34a' },
                          { value: 'missing' as const, label: t('dyeDemand.markMissing'), active: '#dc2626' },
                        ]).map(({ value, label, active }) => {
                          const isActive = row.barwnik_status === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              disabled={saving}
                              title={label}
                              // Ponowny klik w aktywny stan cofa oznaczenie do „niesprawdzone".
                              onClick={() => setDyeStatus(row.item_id, isActive ? 'unknown' : value)}
                              style={{
                                padding: '3px 8px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                borderRadius: '4px',
                                cursor: saving ? 'wait' : 'pointer',
                                opacity: saving ? 0.5 : 1,
                                border: `1px solid ${isActive ? active : '#adb5bd'}`,
                                backgroundColor: isActive ? active : 'white',
                                color: isActive ? 'white' : '#495057',
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td style={{ ...scrollCellStyle(w('dosage')), fontWeight: 'bold', fontSize: '14px', textAlign: 'right' }}>
                      {row.dosage_missing ? (
                        <span style={{ color: '#dc3545' }} title={t('dyeDemand.dosageMissingHint')}>
                          &#9888;&#65039;
                        </span>
                      ) : (
                        row.doz_procent || '-'
                      )}
                    </td>
                    <td style={{ ...scrollCellStyle(w('weight')), textAlign: 'right' }}>{row.waga || '-'}</td>
                    <td style={{ ...scrollCellStyle(w('toProduce')), textAlign: 'right' }}>
                      {row.do_produkcji.toLocaleString('pl-PL')}
                    </td>
                    <td style={{ ...scrollCellStyle(w('remainingQty')), textAlign: 'right' }}>
                      {row.pozostalo.toLocaleString('pl-PL')}
                    </td>
                    <td style={{ ...stickyCellStyle(STICKY_WIDTH.dyeKgRemaining, STICKY_WIDTH.dyeKg, '#fef3c7', true), fontWeight: 'bold' }}>
                      {formatKg(row.dye_kg)}
                    </td>
                    <td style={stickyCellStyle(0, STICKY_WIDTH.dyeKgRemaining, '#dbeafe')}>
                      {formatKg(row.dye_kg_remaining)}
                    </td>
                  </tr>
                );
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px', color: '#1a202c' }}>{t('dyeDemand.title')}</h2>
      <p style={{ marginTop: '-12px', marginBottom: '20px', color: '#718096', fontSize: '14px' }}>
        {t('dyeDemand.subtitle')}
      </p>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#4a5568' }}>
            {t('dyeDemand.filterLine')}
          </label>
          <select value={lineFilter} onChange={(e) => setLineFilter(e.target.value)} style={selectStyle}>
            <option value="">{t('dyeDemand.allLines')}</option>
            {data.available_lines.map((line) => (
              <option key={line.id} value={line.id}>{line.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#4a5568' }}>
            {t('dyeDemand.filterWeek')}
          </label>
          <select value={weekFilter} onChange={(e) => setWeekFilter(e.target.value)} style={selectStyle}>
            <option value="">{t('dyeDemand.allWeeks')}</option>
            {data.available_weeks.map((week) => (
              <option key={week} value={week}>{week}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#4a5568' }}>
            {t('dyeDemand.filterDyeStatus')}
          </label>
          <select value={dyeStatusFilter} onChange={(e) => setDyeStatusFilter(e.target.value)} style={selectStyle}>
            <option value="all">{t('dyeDemand.dyeStatusAll')}</option>
            <option value="missing">{t('dyeDemand.dyeStatusMissing')}</option>
            <option value="available">{t('dyeDemand.dyeStatusAvailable')}</option>
            <option value="unknown">{t('dyeDemand.dyeStatusUnknown')}</option>
          </select>
        </div>
      </div>

      {/* Totals */}
      <div style={{
        display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '16px',
        padding: '14px 18px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px',
      }}>
        <div>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>{t('dyeDemand.totalPlanned')}</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#667eea' }}>{formatKg(data.total_dye_kg)}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>{t('dyeDemand.totalRemaining')}</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#1e293b' }}>{formatKg(data.total_dye_kg_remaining)}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>{t('dyeDemand.totalRows')}</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#1e293b' }}>{data.total.toLocaleString('pl-PL')}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>{t('dyeDemand.totalMissing')}</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: data.missing_rows > 0 ? '#dc2626' : '#1e293b' }}>
            {data.missing_rows.toLocaleString('pl-PL')}
          </div>
        </div>
      </div>

      {/* Jak czytac liste: grupowanie po barwniku + skad bierze sie zielony wiersz. */}
      <div style={{ marginBottom: '8px', fontSize: '12px', color: '#64748b' }}>
        {t('dyeDemand.groupsSubtitle')}
      </div>
      <div style={{ marginBottom: '8px', fontSize: '12px', color: '#64748b' }}>
        {t('dyeDemand.startedAutoGreen')}
      </div>

      {data.truncated && (
        <div style={{ marginBottom: '12px', color: '#e53e3e', fontSize: '13px' }}>
          {t('dyeDemand.truncated')}
        </div>
      )}

      <div>
        <div style={{ width: '100%', marginTop: '20px', overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          <table
            style={{
              width: '100%',
              minWidth: `${tableMinWidth}px`,
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              backgroundColor: 'white',
              fontSize: '12px',
            }}
          >
            <colgroup>
              {SCROLL_COLUMNS.map((col) => (
                <col key={`c-${col.key}`} style={{ width: `${columnWidth(col)}px` }} />
              ))}
              <col style={{ width: `${STICKY_WIDTH.dyeKg}px` }} />
              <col style={{ width: `${STICKY_WIDTH.dyeKgRemaining}px` }} />
            </colgroup>

            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                {SCROLL_COLUMNS.map((col) => (
                  <th key={col.key} style={scrollHeaderStyle(columnWidth(col))}>{t(col.labelKey).toUpperCase()}</th>
                ))}
                {/* Kilogramy barwnika zawsze pod reka — przyklejone do prawej krawedzi,
                    tak jak DO PRODUKCJI / POZOSTALO w „Moja produkcja". */}
                <th style={stickyHeaderStyle(STICKY_WIDTH.dyeKgRemaining, STICKY_WIDTH.dyeKg, '#fbbf24', '#000', true)}>
                  {t('dyeDemand.colDyeKg').toUpperCase()}
                </th>
                <th style={stickyHeaderStyle(0, STICKY_WIDTH.dyeKgRemaining, '#3b82f6', 'white')}>
                  {t('dyeDemand.colDyeKgRemaining').toUpperCase()}
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={SCROLL_COLUMNS.length + 2} style={{ padding: 28, textAlign: 'center', color: '#94a3b8' }}>
                    {t('common.loading')}
                  </td>
                </tr>
              ) : data.results.length === 0 ? (
                <tr>
                  <td colSpan={SCROLL_COLUMNS.length + 2} style={{ padding: 28, textAlign: 'center', color: '#94a3b8' }}>
                    {t('dyeDemand.noData')}
                  </td>
                </tr>
              ) : (
                <>
                  {data.dye_groups.map((group) => renderGroup(
                    group.dye_key,
                    group.barwnik,
                    group.items,
                    group.dye_kg,
                    group.dye_kg_remaining,
                    byDye.get(group.dye_key) ?? [],
                    group.missing_rows > 0,
                    group.dosage_missing,
                  ))}

                  {/* Pozycje z kilkoma barwnikami — widoczne w tej samej liscie, ale poza
                      sumami: dozowania z importu nie da sie przypisac do jednego koloru. */}
                  {multiDyeRows.length > 0 && renderGroup(
                    '__multi__',
                    t('dyeDemand.groupMultiTitle'),
                    data.multi_dye.items,
                    data.multi_dye.dye_kg,
                    data.multi_dye.dye_kg_remaining,
                    multiDyeRows,
                    false,
                    false,
                    true,
                  )}

                  {noDyeRows.length > 0 && renderGroup(
                    '__nodye__',
                    t('dyeDemand.groupNoDyeTitle'),
                    new Set(noDyeRows.map((r) => r.item_id)).size,
                    0,
                    0,
                    noDyeRows,
                    false,
                    false,
                    true,
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default DyeDemand;
